import { ClaimList, formatPatientName } from '@billing/validation';
import { Flex, Loader } from '@mantine/core';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { usePaginateClaimsByPatient } from 'api/resource';
import {
  BillingDataGridWrapper,
  InternalRefreshButton,
} from 'components/common';
import { EOB } from 'interfaces';
import {
  MRT_ColumnDef,
  MRT_ShowHideColumnsButton,
  MRT_TableOptions,
} from 'mantine-react-table';
import { AllKeys } from 'pages/claims/components/ModalComponents';
import React, { useEffect, useMemo, useState } from 'react';
import { useCurrentSiteStore } from 'store';
import { navigateToPath } from 'utils/miscUtils';

export interface ClaimPatientHistoryRow {
  id: string;
  rowType: 'debit' | 'tariff';
  claimID: string;
  claimNumber?: string | null;
  serviceStart: Date;
  status: string | null;
  provider?: {
    title: string | null;
    firstName: string;
    lastName: string;
  };
  payor?: { name: string };
  debit: ClaimPatientHistoryDebit;
  tariff: ClaimPatientHistoryTariff;
}

interface PatientHistoryTableProps {
  patientID: string;
  columns: MRT_ColumnDef<ClaimPatientHistoryRow>[];
  visibleColumns: AllKeys[];
  pageSize?: number;
  queryClient?: QueryClient;
  onTotalChange?: (total: number) => void;
}

interface ClaimPatientHistoryDebit {
  id: string;
  tariffID: string | null;
  requestedAmount: string | null;
  paidAmount: string | null;
  status: 'processed' | 'pending' | null;
  eobs: EOB[];
}

interface ClaimPatientHistoryTariff {
  id: string;
  code: string;
  prefix: number;
  type: string;
  rate: string;
  total?: number | null;
  icd9?: string | null;
  status?: string | null;
  latestComment?: string | null;
}

const findTariffForDebit = (
  debit: ClaimList['debits'][number],
  tariffs: ClaimPatientHistoryTariff[]
) => {
  const byID = tariffs.find((tariff) => tariff.id === debit.tariffID);
  if (byID) return byID;

  if (!debit.tariff) return undefined;

  // fallback in case a tariff was updated to a more recent version of the same tariff
  const byCodePrefixType = tariffs.find(
    (tariff) =>
      tariff.code === debit.tariff?.code &&
      tariff.prefix === debit.tariff?.prefix &&
      tariff.type === debit.tariff?.type
  );
  if (byCodePrefixType) return byCodePrefixType;

  return undefined;
};

const toHistoryDebit = (
  debit: ClaimList['debits'][number]
): ClaimPatientHistoryDebit => ({
  id: debit.id,
  tariffID: debit.tariffID,
  requestedAmount: debit.requestedAmount,
  paidAmount: debit.paidAmount,
  status: debit.status,
  eobs: debit.eobs,
});

const createTariffOnlyDebit = (
  tariff: ClaimPatientHistoryTariff
): ClaimPatientHistoryDebit => ({
  id: `tariff-${tariff.id}`,
  tariffID: tariff.id,
  requestedAmount: String(tariff.total ?? tariff.rate),
  paidAmount: '0',
  status: 'pending',
  eobs: [],
});

const createRowBase = (claim: ClaimList) => ({
  claimID: claim.id,
  claimNumber: claim.claimNumber,
  serviceStart: claim.serviceStart,
  status: claim.status,
  provider: claim.provider
    ? {
        title: claim.provider.title,
        firstName: claim.provider.firstName,
        lastName: claim.provider.lastName,
      }
    : undefined,
  payor: claim.payor ? { name: claim.payor.name } : undefined,
});

export const buildClaimPatientHistoryRows = (
  claims: ClaimList[]
): ClaimPatientHistoryRow[] =>
  claims.flatMap<ClaimPatientHistoryRow>((claim) => {
    const claimTariffs: ClaimPatientHistoryTariff[] = claim.tariffs;
    const base = createRowBase(claim);
    const debitRows: ClaimPatientHistoryRow[] = (claim.debits ?? []).flatMap(
      (debit): ClaimPatientHistoryRow[] => {
        const matchedTariff = findTariffForDebit(debit, claimTariffs);
        const debitTariff: ClaimPatientHistoryTariff | undefined = debit.tariff
          ? {
              id: debit.tariff.id ?? debit.tariffID ?? debit.id,
              code: debit.tariff.code ?? '',
              prefix: debit.tariff.prefix ?? 0,
              type: debit.tariff.type ?? '',
              rate: debit.tariff.rate ?? '0',
              icd9: debit.tariff.icd9,
            }
          : undefined;
        const displayTariff = matchedTariff ?? debitTariff;
        const isUnpaidUnlinked =
          !matchedTariff && Number(debit.paidAmount ?? 0) === 0;

        if (isUnpaidUnlinked || !displayTariff) return [];

        return [
          {
            ...base,
            id: debit.id,
            rowType: 'debit',
            debit: toHistoryDebit(debit),
            tariff: displayTariff,
          },
        ];
      }
    );

    if (debitRows.length > 0) return debitRows;

    return claimTariffs.map<ClaimPatientHistoryRow>((tariff) => ({
      ...base,
      id: tariff.id,
      rowType: 'tariff' as const,
      debit: createTariffOnlyDebit(tariff),
      tariff,
    }));
  });

export const ClaimPatientHistoryTable: React.FC<PatientHistoryTableProps> = ({
  patientID,
  columns,
  visibleColumns,
  pageSize = 10,
  queryClient,
  onTotalChange,
}) => {
  const { timezone, id: siteID } = useCurrentSiteStore();
  const contextQueryClient = useQueryClient();
  const refreshQueryClient = queryClient ?? contextQueryClient;

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: pageSize,
    globalFilter: '',
    columnFilters: [],
    sorting: [],
  });

  const { data, isLoading, isFetching } = usePaginateClaimsByPatient(
    patientID,
    siteID,
    pagination.pageIndex,
    pagination.pageSize,
    timezone,
    pagination.columnFilters,
    pagination.globalFilter,
    pagination.sorting
  );

  const [patient, setPatient] = useState<{
    firstName: string;
    lastName: string;
  } | null>(null);

  useEffect(() => {
    if (data?.data && data.data.length > 0) {
      const firstClaim = data.data[0];
      if (firstClaim.patient) {
        setPatient({
          firstName: firstClaim.patient.firstName,
          lastName: firstClaim.patient.lastName,
        });
      }
    }
  }, [data]);

  useEffect(() => {
    if (data) onTotalChange?.(data.total);
  }, [data, onTotalChange]);

  const patientName = formatPatientName(
    patient?.firstName ?? '',
    patient?.lastName ?? ''
  );
  const title = `${patientName ? `${patientName} ` : ''}Claim History`;

  const defaultVisibility = useMemo(() => {
    return columns.reduce<Record<string, boolean>>((vis, col) => {
      vis[col.accessorKey as string] = visibleColumns.includes(
        col.accessorKey as any
      );
      return vis;
    }, {});
  }, [columns, visibleColumns]);

  const rows = useMemo(
    () => buildClaimPatientHistoryRows(data?.data ?? []),
    [data?.data]
  );

  const tableOptions: MRT_TableOptions<ClaimPatientHistoryRow> = {
    data: rows,
    columns,
    initialState: {
      columnVisibility: defaultVisibility,
    },
    state: {
      pagination,
      showSkeletons: isLoading || isFetching,
    },
    renderToolbarInternalActions: ({ table }) => (
      <>
        <MRT_ShowHideColumnsButton table={table} />
        <InternalRefreshButton queryClient={refreshQueryClient} />
      </>
    ),
    rowCount: data?.total ?? 0,
    manualPagination: true,
    onPaginationChange: setPagination,
    enableRowActions: false,
    enableColumnActions: false,
    mantineTableBodyRowProps: ({ row }) => ({
      onDoubleClick: () => {
        navigateToPath({ path: `/claims/edit/${row.original.claimID}` });
      },
    }),
  };

  if (isLoading) {
    return (
      <Flex justify="center" align="center" style={{ height: '100%' }}>
        <Loader type="bars" />
      </Flex>
    );
  }
  return (
    <BillingDataGridWrapper
      title={title}
      tableOptions={tableOptions}
      dataTestID="patient-claim-history"
      showAlertBanner={false}
    />
  );
};
